import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, authHeaders } from "@/lib/api";
import { timeAgo } from "@/lib/timeAgo";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Store, ExternalLink, Copy, CheckCircle2, ShieldCheck, Star, Clock, QrCode, Info, Share2, MessageCircle, Mail, MessageSquare, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import LeaveReviewDialog from "@/components/LeaveReviewDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageLightbox from "@/components/ImageLightbox";

const couponValue = (c) => {
  if (!c) return "";
  const t = c.discount_type || "percent";
  const v = Number(c.discount_value ?? c.discount ?? 0);
  if (t === "bogo") return "2x1";
  if (t === "price") return v > 0 ? `${Number.isInteger(v) ? v : v.toFixed(2)}€` : "";
  if (t === "fixed" || t === "amount") return v > 0 ? `-${Number.isInteger(v) ? v : v.toFixed(2)}€` : "";
  return v > 0 ? `-${Math.round(v)}%` : "";
};
const fmtExpiry = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };
const isExpired = (iso) => { try { return iso && new Date(iso) < new Date(); } catch { return false; } };

export default function CouponRedeem() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tok, setTok] = useState(null);
  const [claim, setClaim] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [showCode, setShowCode] = useState(searchParams.get("show") === "1");
  const [loading, setLoading] = useState(true);
  const [used, setUsed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [lastReview, setLastReview] = useState(null);
  const [myReviews, setMyReviews] = useState([]);
  const [reviewableCount, setReviewableCount] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const timer = useRef(null);

  const fetchMyReview = useCallback(async (vendorId) => {
    if (!vendorId) return;
    try {
      const r = await api.get(`/stores/${vendorId}/reviews`, { headers: authHeaders("client") });
      const mine = r.data?.my_reviews || [];
      setMyReviews(mine);
      setLastReview(mine[0] || null);
      setReviewableCount(r.data?.reviewable_count || 0);
    } catch (_) {}
  }, []);

  const fetchToken = useCallback(async () => {
    try {
      const r = await api.get(`/client/claims/${claimId}/token`, { headers: authHeaders("client") });
      setTok(r.data);
      if (!r.data.online) setSecondsLeft(r.data.seconds_left || 30);
      if (r.data.redeemed) setUsed(true);
    } catch (err) {
      if (err?.response?.status === 410) setUsed(true);
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    api.get("/client/claims", { headers: authHeaders("client") })
      .then((r) => {
        const c = (r.data.claims || []).find((c) => c.claim_id === claimId);
        setClaim(c);
        if (c?.vendor_id) fetchMyReview(c.vendor_id);
        if (c?.offer_id) api.get(`/offers/${c.offer_id}`).then((o) => setOfferData(o.data)).catch(() => {});
      })
      .catch(() => {});
    fetchToken();
  }, [claimId, fetchToken, fetchMyReview]);

  // Countdown + auto refresh rotating code
  useEffect(() => {
    if (!tok || tok.online || used) return;
    timer.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { fetchToken(); return tok.period || 30; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [tok, used, fetchToken]);

  const markUsed = async () => {
    try {
      await api.post(`/client/claims/${claimId}/mark-used`, {}, { headers: authHeaders("client") });
      setUsed(true);
      toast.success("Coupon segnato come usato");
    } catch (_) {}
  };

  const copyCode = async (code) => { try { await navigator.clipboard.writeText(code); toast.success("Codice copiato"); } catch (_) { toast.error("Copia non riuscita"); } };

  const offerId = offerData?.offer_id || claim?.offer_id;
  const shareUrl = offerId ? `${window.location.origin}/offer/${offerId}` : window.location.origin;
  const shareText = `Guarda questa offerta su KoupoNow: "${claim?.title || "Offerta"}" da ${claim?.store_name || "un negozio"}. Scaricala anche tu 👉 ${shareUrl}`;
  const shareWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank"); setShareOpen(false); };
  const shareSMS = () => { window.location.href = `sms:?&body=${encodeURIComponent(shareText)}`; setShareOpen(false); };
  const shareEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent("Un'offerta per te su KoupoNow")}&body=${encodeURIComponent(shareText)}`; setShareOpen(false); };
  const shareNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: "KoupoNow", text: shareText, url: shareUrl }); setShareOpen(false); return; } catch (_) {} }
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copiato negli appunti"); setShareOpen(false); } catch (_) {}
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  return (
    <div className="kn-fade-up min-h-[70vh]">
      <button onClick={() => navigate("/coupons")} data-testid="coupon-back" className="flex items-center gap-1.5 text-slate-500 font-semibold mb-4">
        <ArrowLeft className="h-5 w-5" /> I miei coupon
      </button>

      <div className="kn-card overflow-hidden max-w-sm mx-auto">
        <div className="kn-gradient px-6 py-5 text-white text-center">
          <div className="inline-flex items-center gap-1.5 text-white/90 text-sm font-semibold">
            <Store className="h-4 w-4" /> {claim?.store_name || "Negozio"}
          </div>
          <h1 className="text-xl font-extrabold mt-1">{claim?.title || "Il tuo coupon"}</h1>
          {couponValue(claim) && <div data-testid="coupon-value" className="mt-2 inline-block bg-white/20 rounded-full px-4 py-1 text-2xl font-extrabold">{couponValue(claim)}</div>}
          {(offerData?.description || claim?.description) && <p className="text-white/85 text-xs mt-2 max-w-[85%] mx-auto break-words whitespace-pre-wrap">{offerData?.description || claim?.description}</p>}
          {claim?.expires_at && <p className={`text-[11px] mt-1.5 ${isExpired(claim.expires_at) ? "text-yellow-200 font-bold" : "text-white/70"}`}>{isExpired(claim.expires_at) ? "Scaduto il " : "Valido fino al "}{fmtExpiry(claim.expires_at)}</p>}
        </div>

        {claim?.daily_start && claim?.daily_end && !used && (
          <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-100" data-testid="coupon-window-reminder">
            <Clock className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm font-bold text-amber-800">Usalo tra le {claim.daily_start} e le {claim.daily_end}</p>
          </div>
        )}

        {((offerData?.images || claim?.images) || []).length > 0 && (
          <div className="flex gap-2 p-3 bg-slate-50 overflow-x-auto" data-testid="coupon-images">
            {(offerData?.images || claim?.images).map((img, i) => (
              <button type="button" key={i} onClick={() => setLightboxIdx(i)}
                className="relative shrink-0 rounded-xl overflow-hidden border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#E63946]"
                data-testid={`coupon-image-${i}`} aria-label="Ingrandisci foto">
                <img src={img} alt="" className="w-40 aspect-video object-cover" />
                <span className="absolute bottom-1 right-1 h-6 w-6 rounded-full bg-black/55 text-white flex items-center justify-center">
                  <Maximize2 className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
          </div>
        )}

        {!used && (offerData?.notes || offerData?.min_spend > 0 || claim?.min_spend > 0) && (
          <div className="px-4 py-3 border-b border-slate-100 space-y-2" data-testid="coupon-conditions">
            {(offerData?.min_spend > 0 || claim?.min_spend > 0) && (
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                <Info className="h-4 w-4 shrink-0" /> Spesa minima {Number(offerData?.min_spend || claim?.min_spend)}€
              </div>
            )}
            {offerData?.notes && (
              <p className="text-sm text-slate-500"><span className="font-semibold text-slate-600">Condizioni: </span>{offerData.notes}</p>
            )}
          </div>
        )}

        {used ? (
          <div className="p-8 text-center">
            <div className="h-20 w-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            </div>
            <p className="font-extrabold text-lg text-slate-800">Coupon utilizzato</p>
            <p className="text-slate-500 text-sm mt-1">Questo coupon è stato riscattato.</p>
            {claim?.vendor_id && (
              <div className="mt-5 rounded-2xl bg-[#FFF1F2] p-4" data-testid="review-section">
                {lastReview ? (
                  <div className="rounded-xl bg-white/70 p-3 mb-3 text-left" data-testid="last-review-box">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-4 w-4 ${n <= (lastReview.rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                        ))}
                      </div>
                      <span className="text-[11px] text-slate-400">{timeAgo(lastReview.created_at)}</span>
                    </div>
                    <p className="text-xs font-bold text-slate-500 mt-1">La tua ultima recensione</p>
                    {lastReview.comment && <p className="text-slate-600 text-sm mt-1 italic break-words">"{lastReview.comment}"</p>}
                  </div>
                ) : (
                  <>
                    <p className="font-bold text-slate-800 text-sm text-center">Com'è andata da {claim?.store_name || "questo negozio"}?</p>
                    <p className="text-slate-500 text-xs mt-0.5 mb-3 text-center">Non hai ancora lasciato nessuna recensione a questo negozio.</p>
                  </>
                )}

                {reviewableCount > 0 ? (
                  <Button onClick={() => { setReviewRating(5); setReviewOpen(true); }} data-testid="review-invite-btn" className="w-full h-11 rounded-xl font-bold">
                    {myReviews.length > 0 ? "Lascia un'altra recensione" : "Lascia la tua prima recensione"}
                  </Button>
                ) : (
                  <p className="text-center text-xs text-slate-400 py-1" data-testid="no-more-reviews">Hai recensito tutti i tuoi coupon di questo negozio. Grazie!</p>
                )}
                <Button variant="ghost" onClick={() => navigate("/")} data-testid="review-later-btn" className="w-full h-10 rounded-xl font-semibold text-slate-500 mt-2">
                  La prossima volta
                </Button>
              </div>
            )}
          </div>
        ) : !showCode ? (
          <div className="p-6 text-center space-y-4" data-testid="coupon-reveal">
            <p className="text-sm text-slate-500">
              {tok?.online ? "Pronto per l'acquisto online?" : "Sei in cassa? Mostra il codice da far scansionare al negozio."}
            </p>
            <Button onClick={() => setShowCode(true)} data-testid="reveal-code-btn" className="w-full h-12 rounded-xl font-extrabold text-base gap-2">
              <QrCode className="h-5 w-5" /> {tok?.online ? "Mostra codice sconto" : "Mostra codice in cassa"}
            </Button>
            <p className="text-xs text-slate-400">
              {tok?.online ? "Riceverai un codice da usare sul sito del negozio." : "Il codice QR cambia ogni 30 secondi: mostralo solo al momento del pagamento."}
            </p>
          </div>
        ) : tok?.online ? (
          <div className="p-6 text-center space-y-4">
            <p className="text-sm text-slate-500">Usa questo codice sul sito del negozio</p>
            <div className="text-3xl font-extrabold tracking-widest text-slate-900 bg-slate-100 rounded-xl py-4">{tok.code}</div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-11 rounded-xl gap-1.5" onClick={() => copyCode(tok.code)} data-testid="copy-online-code">
                <Copy className="h-4 w-4" /> Copia
              </Button>
              {tok.shop_url && (
                <a href={tok.shop_url} target="_blank" rel="noreferrer" className="flex-1">
                  <Button className="w-full h-11 rounded-xl gap-1.5" data-testid="open-shop-url"><ExternalLink className="h-4 w-4" /> Vai al sito</Button>
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center space-y-4" data-testid="rotating-coupon">
            <div className="bg-white rounded-2xl p-4 inline-block border border-slate-100 kn-pop" key={tok?.qr}>
              <QRCodeSVG value={tok?.qr || ""} size={188} fgColor="#0F172A" level="M" />
            </div>
            <div>
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Codice a rotazione</p>
              <div className="text-4xl font-extrabold tracking-[0.35em] text-[#E63946]" data-testid="otp-code">{tok?.code}</div>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
              <div className="h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full kn-gradient transition-all duration-1000" style={{ width: `${(secondsLeft / (tok?.period || 30)) * 100}%` }} />
              </div>
              <span className="font-mono">{secondsLeft}s</span>
            </div>
            <div className="flex items-start gap-2 text-left text-xs text-slate-500 bg-slate-50 rounded-xl p-3">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />
              Mostra il QR (o detta le 6 cifre) in cassa. Cambia ogni 30 secondi per la tua sicurezza.
            </div>
            <Button variant="outline" onClick={markUsed} data-testid="mark-used-btn" className="w-full h-11 rounded-xl">
              Segna come usato
            </Button>
          </div>
        )}
      </div>

      <button
        onClick={() => setShareOpen(true)}
        data-testid="share-coupon-btn"
        className="mt-4 mx-auto flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:border-[#E63946] transition-colors"
      >
        <Share2 className="h-4 w-4 text-[#E63946]" /> Condividi con un amico
      </button>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-sm rounded-3xl" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-extrabold">Condividi questa offerta</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Invita un amico: potrà scaricare il suo coupon. Il tuo codice resta privato.</p>
          <div className="space-y-2 mt-2">
            <button onClick={shareWhatsApp} data-testid="share-whatsapp" className="w-full flex items-center gap-3 h-12 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 px-4 font-bold text-slate-800 transition-colors">
              <MessageCircle className="h-5 w-5 text-[#25D366]" /> WhatsApp
            </button>
            <button onClick={shareSMS} data-testid="share-sms" className="w-full flex items-center gap-3 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 px-4 font-bold text-slate-800 transition-colors">
              <MessageSquare className="h-5 w-5 text-slate-600" /> SMS
            </button>
            <button onClick={shareEmail} data-testid="share-email" className="w-full flex items-center gap-3 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 px-4 font-bold text-slate-800 transition-colors">
              <Mail className="h-5 w-5 text-slate-600" /> Email
            </button>
            <button onClick={shareNative} data-testid="share-more" className="w-full flex items-center gap-3 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 px-4 font-bold text-slate-800 transition-colors">
              <Share2 className="h-5 w-5 text-slate-600" /> Altro / Copia link
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {claim?.vendor_id && (
        <LeaveReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} vendorId={claim.vendor_id}
          storeName={claim.store_name} initialRating={reviewRating}
          claimId={myReviews.some((r) => r.claim_id === claimId) ? undefined : claimId}
          onDone={() => navigate("/")} />
      )}

      <ImageLightbox images={(offerData?.images || claim?.images) || []} index={lightboxIdx} onClose={() => setLightboxIdx(null)} />
    </div>
  );
}
